import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { Error } from "../../../../../../../models/error";
import { LeadSource } from "../../../../../../../models/leadsource";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import LeadSourceService from "../../../../../../../services/lead.source.from.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["lead.source.list", page, pageSize, filters, searches, sorting],
    () =>
      LeadSourceService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["id", "desc"]])
          : JSON.stringify([["id", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setLeadSources(response.data);
      },
    }
  );

  const { refetch: getLeadSourceTotal } = useQuery(
    ["lead.source.count", filters, searches],
    () =>
      LeadSourceService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createLeadSource, isLoading: isCreating } = useMutation(
    ["lead.source.create"],
    (leadSource: LeadSource) => LeadSourceService.create(leadSource),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getLeadSourceTotal();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateLeadSource, isLoading: isUpdating } = useMutation(
    ["lead.source.update"],
    ({ id, leadSource }: { id: number; leadSource: LeadSource }) =>
      LeadSourceService.update(id, leadSource),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getLeadSourceTotal();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    leadSources,
    total,
    page,
    pageSize,
    hasErrors,
    isLoading: isFetching || isCreating || isUpdating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createLeadSource,
    updateLeadSource,
  };
};

export default useActions;
